import React from 'react';
import {
  TableModule,
  TableConfiguration,
} from '@lifeomic/chroma-react/components/TableModule';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import TableTitle from '@lifeomic/phc-web-toolkit/src/components/Table/TableTitle';
import { useActiveProject } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProject';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import { usePagination } from '@lifeomic/phc-web-toolkit/src/services/util/usePagination';
import SummaryService from '@lifeomic/phc-web-toolkit/src/services/SummaryService';
import {
  getPrevalenceTableData,
  getMediaTableData,
  getPatientPieData,
  getPatientStatusData,
  getPatientBarData,
  getPatientAgeData,
} from './PlotDataProcessor';
import { useActiveProjectCohortId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { PieChart } from './PieChart';
import { StackedBarChart } from './StackedBarChart';

const summaryService = new SummaryService();
const PATIENT_COUNT = 'patient_count';
const MEDIA_COUNT = 'media_type_count';

const messages = defineMessages({
  prevalenceLabel: {
    id: 'pages.summary.clinical.labels.prevalence',
    defaultMessage: 'Prevalence',
  },
  patientsLabel: {
    id: 'pages.summary.clinical.labels.patients',
    defaultMessage: 'Patients',
  },
  conditionLabel: {
    id: 'pages.summary.clinical.labels.condition',
    defaultMessage: 'Condition',
  },
  procedureLabel: {
    id: 'pages.summary.clinical.labels.procedure',
    defaultMessage: 'Procedure',
  },
  medicationLabel: {
    id: 'pages.summary.clinical.labels.medication',
    defaultMessage: 'Medication',
  },
  observationLabel: {
    id: 'pages.summary.clinical.labels.observation',
    defaultMessage: 'Observation',
  },
  mediaTypeLabel: {
    id: 'pages.summary.clinical.labels.media',
    defaultMessage: 'Media Type',
  },
  countLabel: {
    id: 'pages.summary.clinical.labels.count',
    defaultMessage: 'Count',
  },
  conditionsTitle: {
    id: 'pages.summary.clinical.titles.conditions',
    defaultMessage: 'Conditions',
  },
  proceduresTitle: {
    id: 'pages.summary.clinical.titles.procedures',
    defaultMessage: 'Procedures',
  },
  medicationsTitle: {
    id: 'pages.summary.clinical.titles.medications',
    defaultMessage: 'Medications',
  },
  observationTitle: {
    id: 'pages.summary.clinical.titles.observations',
    defaultMessage: 'Observations',
  },
  mediaTitle: {
    id: 'pages.summary.clinical.titles.media',
    defaultMessage: 'Media',
  },
  ageTitle: {
    id: 'pages.summary.clinical.titles.age',
    defaultMessage: 'Age',
  },
  ageRangeTitle: {
    id: 'pages.summary.clinical.titles.ageRange',
    defaultMessage: 'Age (Ranges)',
  },
  genderTitle: {
    id: 'pages.summary.clinical.titles.gender',
    defaultMessage: 'Gender',
  },
  statusTitle: {
    id: 'pages.summary.clinical.titles.status',
    defaultMessage: 'Status',
  },
  raceTitle: {
    id: 'pages.summary.clinical.titles.race',
    defaultMessage: 'Race',
  },
  ethnicityTitle: {
    id: 'pages.summary.clinical.titles.ethnicity',
    defaultMessage: 'Ethnicity',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.spacing(2),
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(0.25, 0, 2, 0),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    '&:last-of-type': {
      gridColumn: 'span 2',
    },
  },
  row: {
    display: 'flex',
    overflowX: 'auto',
    // Note: This minHeight is to ensure container size doesn't shift
    // when loading and then the data rendering
    minHeight: theme.pxToRem(176),
    padding: theme.spacing(1.5),
    background: theme.palette.black[50],
  },
  ma: {
    margin: 'auto',
  },
  tableContainer: {
    overflow: 'auto',
    [theme.breakpoints.down('xl')]: {
      maxHeight: `60vh`,
    },
    [theme.breakpoints.up('lg')]: {
      maxHeight: '30vh',
    },
  },
}));

function getPrevalenceTableConfiguration(
  resource: any,
  patientsLabel: any,
  prevalenceLabel: any,
) {
  const config: Array<TableConfiguration> = [
    {
      header: {
        label: resource,
      },
      cell: {
        align: 'left',
        content: (dataValue: any) => {
          return dataValue.name;
        },
      },
    },
    {
      header: {
        label: patientsLabel,
        align: 'right',
      },
      cell: {
        align: 'right',
        content: (dataValue: any) => {
          return dataValue.patients;
        },
      },
    },
    {
      header: {
        label: prevalenceLabel,
        align: 'right',
      },
      cell: {
        align: 'right',
        content: (dataValue: any) => {
          return dataValue.prevalence;
        },
      },
    },
  ];
  return config;
}

function getCountTableConfiguration(resource: any, countLabel: any) {
  const config: Array<TableConfiguration> = [
    {
      header: {
        label: resource,
      },
      cell: {
        align: 'left',
        content: (dataValue: any) => {
          return dataValue.name;
        },
      },
    },
    {
      header: {
        label: countLabel,
        align: 'right',
      },
      cell: {
        align: 'right',
        content: (dataValue: any) => {
          return dataValue.count;
        },
      },
    },
  ];
  return config;
}

export const ClinicalVisuals = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  resources, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  isLoading,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const [activeProject] = useActiveProject();
  const cohortId = useActiveProjectCohortId();

  const patientsLabel = formatMessage(messages.patientsLabel);
  const prevalenceLabel = formatMessage(messages.prevalenceLabel);

  // medications
  const {
    hasMoreToLoad: hasMoreToLoadMed,
    isLoading: isLoadingMed,
    loadMore: loadMoreMed,
    makeFetchRequest: makeFetchRequestMed,
    results: resultsMed,
  } = usePagination(summaryService.getMedicationSummary);

  React.useEffect(() => {
    if (!activeProject) {
      return;
    }
    makeFetchRequestMed({
      projectId: activeProject.id,
      orderBy: PATIENT_COUNT,
      cohortId,
    });
  }, [activeProject, cohortId, makeFetchRequestMed]);

  // procedures
  const {
    hasMoreToLoad: hasMoreToLoadProc,
    isLoading: isLoadingProc,
    loadMore: loadMoreProc,
    makeFetchRequest: makeFetchRequestProc,
    results: resultsProc,
  } = usePagination(summaryService.getProcedureSummary);

  React.useEffect(() => {
    if (!activeProject) {
      return;
    }
    makeFetchRequestProc({
      projectId: activeProject.id,
      orderBy: PATIENT_COUNT,
      cohortId,
    });
  }, [activeProject, cohortId, makeFetchRequestProc]);

  // conditions
  const {
    hasMoreToLoad: hasMoreToLoadCond,
    isLoading: isLoadingCond,
    loadMore: loadMoreCond,
    makeFetchRequest: makeFetchRequestCond,
    results: resultsCond,
  } = usePagination(summaryService.getConditionSummary);

  React.useEffect(() => {
    if (!activeProject) {
      return;
    }
    makeFetchRequestCond({
      projectId: activeProject.id,
      orderBy: PATIENT_COUNT,
      cohortId,
    });
  }, [activeProject, cohortId, makeFetchRequestCond]);

  // observations
  const {
    hasMoreToLoad: hasMoreToLoadObs,
    isLoading: isLoadingObs,
    loadMore: loadMoreObs,
    makeFetchRequest: makeFetchRequestObs,
    results: resultsObs,
  } = usePagination(summaryService.getObservationSummary);

  React.useEffect(() => {
    if (!activeProject) {
      return;
    }
    makeFetchRequestObs({
      projectId: activeProject.id,
      orderBy: PATIENT_COUNT,
      cohortId,
    });
  }, [activeProject, cohortId, makeFetchRequestObs]);

  // media
  const {
    hasMoreToLoad: hasMoreToLoadMedia,
    isLoading: isLoadingMedia,
    loadMore: loadMoreMedia,
    makeFetchRequest: makeFetchRequestMedia,
    results: resultsMedia,
  } = usePagination(summaryService.getMediaSummary);

  React.useEffect(() => {
    if (!activeProject) {
      return;
    }
    makeFetchRequestMedia({
      projectId: activeProject.id,
      orderBy: MEDIA_COUNT,
      cohortId,
    });
  }, [activeProject, cohortId, makeFetchRequestMedia]);

  return (
    <div className={classes.row}>
      {isLoading && (
        <Box
          className={classes.ma}
          fullWidth
          fullHeight
          align="center"
          justify="center"
        >
          <DotLoader size={1} />
        </Box>
      )}
      {!isLoading && resources.clinical && (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <TableTitle title={formatMessage(messages.medicationsTitle)}>
              <div className={classes.tableContainer}>
                <InfiniteScroll
                  hasNextPage={hasMoreToLoadMed}
                  loading={isLoadingMed}
                  loadMore={loadMoreMed}
                  scrollOffset={100}
                  scrollContainer="parent"
                >
                  <TableModule
                    config={getPrevalenceTableConfiguration(
                      formatMessage(messages.medicationLabel),
                      patientsLabel,
                      prevalenceLabel,
                    )}
                    data={getPrevalenceTableData(
                      resources.clinical,
                      resultsMed,
                    )}
                    isLoading={isLoadingMed}
                    maxCellWidth={1}
                  />
                </InfiniteScroll>
              </div>
            </TableTitle>
          </Paper>
          <Paper className={classes.paper}>
            <TableTitle title={formatMessage(messages.proceduresTitle)}>
              <div className={classes.tableContainer}>
                <InfiniteScroll
                  hasNextPage={hasMoreToLoadProc}
                  loading={isLoadingProc}
                  loadMore={loadMoreProc}
                  scrollOffset={100}
                  scrollContainer="parent"
                >
                  <TableModule
                    config={getPrevalenceTableConfiguration(
                      formatMessage(messages.procedureLabel),
                      patientsLabel,
                      prevalenceLabel,
                    )}
                    data={getPrevalenceTableData(
                      resources.clinical,
                      resultsProc,
                    )}
                    isLoading={isLoadingProc}
                    maxCellWidth={1}
                  />
                </InfiniteScroll>
              </div>
            </TableTitle>
          </Paper>
          <Paper className={classes.paper}>
            <TableTitle title={formatMessage(messages.conditionsTitle)}>
              <div className={classes.tableContainer}>
                <InfiniteScroll
                  hasNextPage={hasMoreToLoadCond}
                  loading={isLoadingCond}
                  loadMore={loadMoreCond}
                  scrollOffset={100}
                  scrollContainer="parent"
                >
                  <TableModule
                    config={getPrevalenceTableConfiguration(
                      formatMessage(messages.conditionLabel),
                      patientsLabel,
                      prevalenceLabel,
                    )}
                    data={getPrevalenceTableData(
                      resources.clinical,
                      resultsCond,
                    )}
                    isLoading={isLoadingCond}
                    maxCellWidth={1}
                  />
                </InfiniteScroll>
              </div>
            </TableTitle>
          </Paper>
          <Paper className={classes.paper}>
            <TableTitle title={formatMessage(messages.observationTitle)}>
              <div className={classes.tableContainer}>
                <InfiniteScroll
                  hasNextPage={hasMoreToLoadObs}
                  loading={isLoadingObs}
                  loadMore={loadMoreObs}
                  scrollOffset={100}
                  scrollContainer="parent"
                >
                  <TableModule
                    config={getPrevalenceTableConfiguration(
                      formatMessage(messages.observationLabel),
                      patientsLabel,
                      prevalenceLabel,
                    )}
                    data={getPrevalenceTableData(
                      resources.clinical,
                      resultsObs,
                    )}
                    isLoading={isLoadingObs}
                    maxCellWidth={1}
                  />
                </InfiniteScroll>
              </div>
            </TableTitle>
          </Paper>
          <Paper className={classes.paper}>
            <TableTitle title={formatMessage(messages.mediaTitle)}>
              <div className={classes.tableContainer}>
                <InfiniteScroll
                  hasNextPage={hasMoreToLoadMedia}
                  loading={isLoadingMedia}
                  loadMore={loadMoreMedia}
                  scrollOffset={100}
                  scrollContainer="parent"
                >
                  <TableModule
                    config={getCountTableConfiguration(
                      formatMessage(messages.mediaTypeLabel),
                      formatMessage(messages.countLabel),
                    )}
                    data={getMediaTableData(resultsMedia)}
                    isLoading={isLoadingMedia}
                    maxCellWidth={1}
                  />
                </InfiniteScroll>
              </div>
            </TableTitle>
          </Paper>
          <Paper className={classes.paper}>
            <StackedBarChart
              chartTitle={formatMessage(messages.ageTitle)}
              data={getPatientAgeData(resources?.clinical?.demographic?.age)}
              xtitle={formatMessage(messages.ageTitle)}
              ytitle={formatMessage(messages.countLabel)}
              isLoading={isLoading}
            />
          </Paper>
          <Paper className={classes.paper}>
            <PieChart
              chartTitle={formatMessage(messages.ageRangeTitle)}
              data={getPatientPieData(
                resources?.clinical?.demographic?.ageBins,
              )}
              isLoading={isLoading}
            />
          </Paper>
          <Paper className={classes.paper}>
            <PieChart
              chartTitle={formatMessage(messages.genderTitle)}
              data={getPatientPieData(resources?.clinical?.demographic?.gender)}
              isLoading={isLoading}
            />
          </Paper>
          <Paper className={classes.paper}>
            <PieChart
              chartTitle={formatMessage(messages.statusTitle)}
              data={getPatientStatusData(resources?.clinical?.demographic)}
              isLoading={isLoading}
            />
          </Paper>
          <Paper className={classes.paper}>
            <PieChart
              chartTitle={formatMessage(messages.ethnicityTitle)}
              data={getPatientPieData(
                resources?.clinical?.demographic?.ethnicity,
              )}
              isLoading={isLoading}
            />
          </Paper>
          <Paper className={classes.paper}>
            <StackedBarChart
              chartTitle={formatMessage(messages.raceTitle)}
              data={getPatientBarData(resources?.clinical?.demographic?.race)}
              xtitle={formatMessage(messages.raceTitle)}
              ytitle={formatMessage(messages.countLabel)}
              isLoading={isLoading}
            />
          </Paper>
        </div>
      )}
    </div>
  );
};

export default ClinicalVisuals;
